import { getMomentTz } from "@dexteel/mesf-core";
import {
  GridApi,
  ProcessCellForExportParams,
  ProcessHeaderForExportParams,
} from "ag-grid-community";

const moment = getMomentTz();

interface ExcelContentRow {
  cells: Array<{
    data: { type: "String"; value: string };
    mergeAcross?: number;
    styleId?: string;
  }>;
}

interface ExcelExportOptions {
  gridApi: GridApi;
  fileName: string;
  sheetName: string;
  startDate?: Date;
  endDate?: Date;
  userName?: {
    label: string;
    value: string;
  };
  additionalPrependContent?: Array<{ label?: string; value: string }>;
  additionalAppendContent?: Array<{ label?: string; value: string }>;
  processCellCallback?: (cellParams: ProcessCellForExportParams) => string;
  columnKeys?: string[];
}

export const exportToExcel = ({
  gridApi,
  fileName,
  sheetName,
  startDate,
  endDate,
  userName,
  additionalPrependContent = [],
  additionalAppendContent = [],
  processCellCallback,
  columnKeys,
}: ExcelExportOptions): boolean => {
  try {
    let exportFileName = fileName;
    if (startDate) {
      const startDateStr = moment(startDate).format("YYYY-MM-DD");
      exportFileName = `${exportFileName}_${startDateStr}`;
      if (endDate) {
        const endDateStr = moment(endDate).format("YYYY-MM-DD");
        exportFileName = `${exportFileName}_to_${endDateStr}`;
      }
    }
    exportFileName = `${exportFileName}.xlsx`;

    const prependContent: ExcelContentRow[] = [
      {
        cells: [
          {
            styleId: "header",
            data: { type: "String", value: sheetName },
            mergeAcross: 4,
          },
        ],
      },
    ];

    if (startDate) {
      let dateRange = moment(startDate).format("YYYY-MM-DD");
      if (endDate) {
        dateRange += ` to ${moment(endDate).format("YYYY-MM-DD")}`;
      }
      prependContent.push({
        cells: [
          {
            data: { type: "String", value: `Date: ${dateRange}` },
            mergeAcross: 3,
          },
        ],
      });
    }

    if (userName) {
      prependContent.push({
        cells: [
          {
            data: {
              type: "String",
              value: `${userName.label}: ${userName.value}`,
            },
            mergeAcross: 3,
          },
        ],
      });
    }

    additionalPrependContent.forEach((content) => {
      prependContent.push({
        cells: [
          {
            data: {
              type: "String",
              value: content.label
                ? `${content.label}: ${content.value}`
                : content.value,
            },
            mergeAcross: 3,
          },
        ],
      });
    });

    prependContent.push({ cells: [] });

    const appendContent: ExcelContentRow[] = [
      { cells: [] },
      {
        cells: [
          {
            data: {
              type: "String",
              value: `Generated on: ${new Date().toLocaleString()}`,
            },
            mergeAcross: 3,
          },
        ],
      },
    ];

    additionalAppendContent.forEach((content) => {
      appendContent.push({
        cells: [
          {
            data: {
              type: "String",
              value: content.label
                ? `${content.label}: ${content.value}`
                : content.value,
            },
            mergeAcross: 3,
          },
        ],
      });
    });

    const params: any = {
      fileName: exportFileName,
      sheetName,
      exportMode: "xlsx" as const,
      prependContent,
      appendContent,
      processHeaderCallback: (params: ProcessHeaderForExportParams) => {
        return params.column.getColDef().headerName || "";
      },
      processCellCallback:
        processCellCallback ||
        ((cellParams: ProcessCellForExportParams) => cellParams.value ?? ""),
    };

    if (columnKeys && columnKeys.length > 0) {
      params.columnKeys = columnKeys;
    }

    gridApi.exportDataAsExcel(params);
    return true;
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    throw error;
  }
};
